import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, UserCheck, ArrowRight, Check, Calendar } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { apiMembership, apiWeeklyUpdates } from "@/lib/api";

function FeatureCard({ tag, title, description, features, ctaLabel, onClick, accent, testid, ctaTestid, IconEl }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="border border-neutral-900 bg-neutral-950 p-7 flex flex-col hover:border-neutral-700 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3">
        {IconEl && (
          <span className="inline-flex items-center justify-center w-6 h-6 border border-neutral-800" style={{ color: accent }}>
            <IconEl className="w-3.5 h-3.5" />
          </span>
        )}
        <div className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: accent }}>
          {tag}
        </div>
      </div>
      <h2 className="font-black text-2xl sm:text-3xl tracking-tight text-white mb-3">
        {title}
      </h2>
      <p className="text-sm text-neutral-400 leading-relaxed mb-5">
        {description}
      </p>
      <ul className="space-y-2 mb-7 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-center gap-2 text-sm text-neutral-300">
            <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: accent }} />
            {f}
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        data-testid={ctaTestid}
        className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 transition-colors"
      >
        {ctaLabel} <ArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

export default function Landing({ onSelectExpert, onSelectMarket }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isActiveMember, setIsActiveMember] = useState(false);
  const [latestUpdates, setLatestUpdates] = useState([]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const m = await apiMembership();
        const active = !!m?.active;
        setIsActiveMember(active);
        if (active) {
          try { setLatestUpdates((await apiWeeklyUpdates() || []).slice(0, 3)); } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    })();
  }, [user]);

  const go = (target) => {
    if (user) {
      navigate(target);
    } else {
      navigate(`/signup?after=${encodeURIComponent(target)}`);
    }
  };

  const handleExpert = onSelectExpert || (() => go("/expert-review"));
  const handleMarket = onSelectMarket || (() => go("/market"));

  return (
    <section className="pt-14 md:pt-20 max-w-5xl mx-auto">
      {/* Weekly Updates (above 'Choose Your Analysis') */}
      <div className="mb-10 border border-neutral-900 bg-gradient-to-b from-[#00E5FF]/[0.03] to-transparent p-5 sm:p-6" data-testid="weekly-updates-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-2 inline-flex items-center gap-1.5">
              <Calendar className="w-3 h-3" /> weekly updates
            </div>
            <h2 className="text-xl sm:text-2xl font-black tracking-tighter text-white leading-tight">
              {isActiveMember ? "Read this week's outlook" : "Weekly market outlooks — members only"}
            </h2>
            {isActiveMember && latestUpdates.length > 0 ? (
              <div className="mt-2 text-xs text-neutral-400">
                Latest: <span className="text-white font-medium">{latestUpdates[0].subject}</span>
              </div>
            ) : (
              <div className="mt-2 text-xs text-neutral-400 max-w-xl">
                Weekly Bitcoin outlooks, market updates, and platform announcements — sent straight to active members.
              </div>
            )}
          </div>
          <button
            onClick={() => navigate(isActiveMember ? "/weekly-updates" : "/membership")}
            data-testid="weekly-updates-cta"
            className="inline-flex items-center justify-center gap-1.5 bg-white text-black font-bold text-xs uppercase tracking-wider px-4 py-2.5 hover:bg-neutral-200 transition-colors shrink-0"
          >
            {isActiveMember ? "Read updates" : "Become a member"} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="text-center mb-12">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-4">
          ── choose your analysis ──
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
          AI speed.
          <br />
          Human depth.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-neutral-400 max-w-2xl mx-auto leading-relaxed">
          Two ways to read any chart. Pick the level of detail and confidence you need.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
        <FeatureCard
          testid="card-market"
          ctaTestid="cta-market"
          tag="LIVE MARKET"
          title="Analyze live crypto markets"
          description="Pick a Binance pair and timeframe. ChartLenz preprocesses 300 live candles — indicators, swings, S/R — then runs the engine."
          features={[
            "Live Binance Spot data",
            "Deterministic S/R + swings",
            "Structural invalidation & stop cap",
            "Trade plan with real prices",
          ]}
          ctaLabel="Open Market"
          accent="#00E5FF"
          onClick={handleMarket}
          IconEl={Activity}
        />
        <FeatureCard
          testid="card-expert"
          ctaTestid="cta-expert"
          tag="EXPERT REVIEW"
          title="Human trader review"
          description="A human trader analyses your asset across multiple timeframes using full market structure — the depth AI cannot replicate."
          features={[
            "Multi-Timeframe Analysis",
            "Market Structure",
            "Trade Ideas",
            "Risk Management",
          ]}
          ctaLabel="Request Review"
          accent="#FFB000"
          onClick={handleExpert}
          IconEl={UserCheck}
        />
      </div>

      {/* Membership Includes + price bar — hidden for active members */}
      {!isActiveMember && (<>
      <div className="mt-10 border border-neutral-900 bg-neutral-950 p-7" data-testid="membership-includes">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-5 text-center">
          ── membership includes ──
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="flex items-start gap-3" data-testid="member-benefit-ai">
            <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#00FF66" }} />
            <div>
              <div className="font-bold text-white">Unlimited AI Analysis</div>
              <div className="text-xs font-mono uppercase tracking-[0.15em] text-neutral-500 mt-0.5">Live market data</div>
              <div className="text-xs text-neutral-400 mt-2 leading-relaxed">
                Run as many Live Market analyses on Binance data as you want — no per-use cost.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3" data-testid="member-benefit-reviews">
            <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#00FF66" }} />
            <div>
              <div className="font-bold text-white">2 Expert Reviews Per Month</div>
              <div className="text-xs font-mono uppercase tracking-[0.15em] text-neutral-500 mt-0.5">
                delivered within 3 business days
              </div>
              <div className="text-xs text-neutral-400 mt-2 leading-relaxed">
                Two deep, human-trader reviews included every month — perfect
                when you want a second pair of eyes across full market structure.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3" data-testid="member-benefit-updates">
            <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#00FF66" }} />
            <div>
              <div className="font-bold text-white">Weekly Bitcoin Market Outlooks</div>
              <div className="text-xs font-mono uppercase tracking-[0.15em] text-neutral-500 mt-0.5">
                members only
              </div>
              <div className="text-xs text-neutral-400 mt-2 leading-relaxed">
                Weekly Bitcoin outlooks covering key support, resistance,
                and scenario updates — straight from the trader.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Price reveal — small, supportive */}
      <div className="mt-5 border border-neutral-900 bg-neutral-950/60 px-7 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="membership-price-bar">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-1">
            ChartLenz Membership
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-black text-2xl sm:text-3xl text-white" data-testid="membership-price-amount">€9.99</span>
            <span className="text-xs font-mono uppercase tracking-[0.15em] text-neutral-500">/ month</span>
          </div>
        </div>
        <div className="text-xs text-neutral-500 sm:text-right max-w-md leading-relaxed">
          Cancel anytime.
        </div>
      </div>
      </>)}
    </section>
  );
}
